import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, catchError, map, tap, config, switchMap } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { TokenPayload } from '../models/token-payload';
import { HubConnection } from '@microsoft/signalr';
import { ConfigService } from '../services/config.service';

@Injectable({ providedIn: 'root' })
export class TokenService {
  private authUrl = '/api/Auth/loginwindows';

  private tokenSubject = new BehaviorSubject<string | null>(null);
  public token$ = this.tokenSubject.asObservable();

  public hubConnection!: HubConnection;

  public hubConnectionSubject = new BehaviorSubject<boolean>(false);
  public OnHubConnected$ = this.hubConnectionSubject.asObservable();

  constructor(private http: HttpClient,
    private configService: ConfigService,
  ) {
    const saved = localStorage.getItem('token');
    if (saved) this.tokenSubject.next(saved);
  }
  public getAuthUrl(): string {
    return this.authUrl;
  }

  public get token(): string | null {
    return this.tokenSubject.getValue();
  }

  public get decodedToken() {
    return jwtDecode<TokenPayload>(this.tokenSubject.getValue()!)
  }

  public isValidTokenSync(): boolean {
    const t = this.token;
    if (!t) return false;
    try {
      const payload = jwtDecode<{ exp?: number }>(t);

      return !!payload.exp && payload.exp * 1000 > Date.now();
    } catch (e) {
      console.error('Невозможно декодировать токен', e);
      return false;
    }
  }

  public updateAuthUrl(): Observable<void> {
    return this.configService.loadAuthConfig().pipe(
      map(() => {
        const authType = this.configService.getAuthType();
        if (authType === 'Windows') {
          this.authUrl = '/api/Auth/loginwindows';
        } else if (authType === 'OAuth') {
          this.authUrl = '/api/Auth/loginoauth';
        } else {
          this.authUrl = '/api/Auth/login';
        }
      })
    );
  }

  public loadBrandingConfig(): Observable<void> {
    return this.configService.loadBrandingConfig().pipe(
      map(() => {})
    );
  }

  public refreshToken(): Observable<boolean> {
    return this.updateAuthUrl().pipe(
      switchMap(() => {
        const authType = this.configService.getAuthType();
        
        // Для OAuth обработка процесса авторизации
        if (authType === 'OAuth') {
          // Проверка возврата из OAuth callback
          const urlParams = new URLSearchParams(window.location.search);
          const isOAuthCallback = urlParams.get('oauth_callback') === 'true';
          
          if (isOAuthCallback) {
            // Попытка получить токен из сессии (после OAuth callback)
            return this.http.get('/api/Auth/oauthtoken', { responseType: 'text' }).pipe(
              tap((token: string) => {
                this.tokenSubject.next(token);
                localStorage.setItem('token', token);
                // Очистка URL
                window.history.replaceState({}, document.title, window.location.pathname);
              }),
              map(() => true),
              catchError(err => {
                // Токен отсутствует в сессии, перенаправление на OAuth логин
                console.error('Не удалось получить OAuth токен из сессии', err);
                window.location.href = this.authUrl;
                return of(false);
              })
            );
          } else {
            // Перенаправление на OAuth логин
            window.location.href = this.authUrl;
            return of(false);
          }
        }
        
        // Для Test и Windows авторизации используется существующий процесс
        return this.http.get(this.authUrl, { responseType: 'text' }).pipe(
          tap((token: string) => {
            this.tokenSubject.next(token);
            localStorage.setItem('token', token);
          }),
          map(() => true),
          catchError(err => {
            console.error('Не удалось получить токен', err);
            return of(false);
          })
        );
      })
    );
  }

  public logout(): void {
    this.tokenSubject.next(null);
    localStorage.removeItem('token');
  }
}
